/**
 * Client-side baseline status fetching.
 *
 * Resolution order (first source that yields ≥1 non-null lift date wins):
 *   1. rpc_v1_baseline_status RPC
 *   2. strength_events table
 *   3. workouts.payload parsing (same logic Home uses for Estimated 1RM)
 *   4. legacy lifts table
 */

import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';
import { V1_LIFT_POOL, type V1Lift } from '@/lib/lifts/canonical';
import { calculateBaselineStatus, type BaselineStatus } from '@/lib/baselineGate';

const log = createLogger('BASELINE');

// ---------------------------------------------------------------------------
// Short-lived in-memory cache (5 min TTL) so completed baselines aren't
// stale-blocked by a prior "not complete" result.
// ---------------------------------------------------------------------------
const _cache = new Map<string, { result: BaselineStatus; ts: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export function clearBaselineCache(userId?: string) {
  if (userId) {
    _cache.delete(userId);
  } else {
    _cache.clear();
  }
}

// ---------------------------------------------------------------------------
// Shared lookup maps
// ---------------------------------------------------------------------------

/** slug-style lift_key values written by record.tsx into strength_events */
const V1_STRENGTH_EVENT_KEYS: Record<V1Lift, string[]> = {
  'back-squat': ['back-squat', 'squat', 'barbell-back-squat'],
  'deadlift': ['deadlift', 'conventional-deadlift', 'barbell-deadlift'],
  'bench-press': ['bench-press', 'bench', 'barbell-bench-press', 'flat-bench-press'],
  'weighted-pull-up': ['weighted-pull-up', 'weighted-chin-up', 'pull-ups', 'pull-up', 'pullup', 'pullups'],
  'dead-hang': ['dead-hang', 'deadhang', 'bar-hang', 'passive-hang', 'hang', 'dead-hang-hold', 'dead_hang'],
};

/** Case-insensitive keyword fragments matched against workout exercise names */
const V1_WORKOUT_KEYWORDS: Record<V1Lift, string[]> = {
  'back-squat': ['back squat', 'barbell back squat', 'squat'],
  'deadlift': ['deadlift', 'dead lift', 'conventional deadlift'],
  'bench-press': ['bench press', 'barbell bench press', 'flat bench'],
  'weighted-pull-up': ['weighted pull-up', 'weighted pull up', 'weighted chin-up', 'weighted chin up', 'pull-ups', 'pull-up', 'pullup', 'pullups'],
  'dead-hang': ['dead hang', 'deadhang', 'bar hang', 'passive hang', 'hang', 'dead hang hold'],
};

/** Exercise names for legacy lifts table join */
const V1_EXERCISE_NAMES: Record<V1Lift, string[]> = {
  'back-squat': ['Back Squat', 'Barbell Back Squat', 'Squat'],
  'deadlift': ['Deadlift', 'Conventional Deadlift', 'Barbell Deadlift'],
  'bench-press': ['Bench Press', 'Barbell Bench Press', 'Flat Bench Press'],
  'weighted-pull-up': ['Weighted Pull-Up', 'Weighted Pull Up', 'Weighted Chin-Up', 'Weighted Chin Up', 'Pull-Ups', 'Pull-Up', 'Pullup', 'Pullups'],
  'dead-hang': ['Dead Hang', 'Deadhang', 'Bar Hang', 'Passive Hang', 'Dead Hang Hold'],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true when at least one V1 lift has a non-null date. */
function hasSignal(dates: Partial<Record<V1Lift, string | null>>): boolean {
  return V1_LIFT_POOL.some((k) => dates[k] != null);
}

// ---------------------------------------------------------------------------
// Tier 1 — RPC
// ---------------------------------------------------------------------------

async function tryRpc(userId: string): Promise<Partial<Record<V1Lift, string | null>> | null> {
  try {
    const { data, error } = await supabase.rpc('rpc_v1_baseline_status', {
      p_user_id: userId,
    });

    if (error || !data || !Array.isArray(data) || data.length === 0) return null;

    const dates: Partial<Record<V1Lift, string | null>> = {};
    for (const row of data) {
      const key = row.v1_lift_key as V1Lift;
      if (V1_LIFT_POOL.includes(key)) {
        dates[key] = row.last_logged_at ?? null;
      }
    }
    return hasSignal(dates) ? dates : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Tier 2 — strength_events
// ---------------------------------------------------------------------------

async function tryStrengthEvents(userId: string): Promise<Partial<Record<V1Lift, string | null>> | null> {
  const dates: Partial<Record<V1Lift, string | null>> = {};

  for (const lift of V1_LIFT_POOL) {
    dates[lift] = null;
    const keys = V1_STRENGTH_EVENT_KEYS[lift];
    try {
      const { data } = await supabase
        .from('strength_events')
        .select('performed_at')
        .eq('profile_id', userId)
        .in('lift_key', keys)
        .order('performed_at', { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        dates[lift] = data[0].performed_at;
      }
    } catch {
      // table may not exist; continue
    }
  }

  return hasSignal(dates) ? dates : null;
}

// ---------------------------------------------------------------------------
// Tier 3 — workouts.payload parsing
// ---------------------------------------------------------------------------

async function tryWorkoutsPayload(userId: string): Promise<Partial<Record<V1Lift, string | null>> | null> {
  const dates: Partial<Record<V1Lift, string | null>> = {};
  for (const lift of V1_LIFT_POOL) dates[lift] = null;

  try {
    const { data: workouts } = await supabase
      .from('workouts')
      .select('performed_at,payload')
      .eq('user_id', userId)
      .order('performed_at', { ascending: false })
      .limit(100);

    if (!workouts || workouts.length === 0) return null;

    // Walk workouts newest-first; for each V1 lift, record the first (most
    // recent) workout that contains a matching exercise name.
    for (const workout of workouts) {
      // All lifts already resolved? Stop early.
      if (V1_LIFT_POOL.every((k) => dates[k] != null)) break;

      let payload: any = workout.payload ?? null;
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch { continue; }
      }
      if (!payload) continue;

      const exercises: any[] = Array.isArray(payload.exercises) ? payload.exercises : [];

      for (const lift of V1_LIFT_POOL) {
        if (dates[lift] != null) continue; // already have a newer date for this lift

        const keywords = V1_WORKOUT_KEYWORDS[lift];
        const match = exercises.some((ex: any) => {
          const name = String(ex?.name ?? '').toLowerCase();
          return keywords.some((kw) => name.includes(kw));
        });

        if (match) {
          dates[lift] = workout.performed_at;
        }
      }
    }
  } catch {
    // ignore query errors
  }

  return hasSignal(dates) ? dates : null;
}

// ---------------------------------------------------------------------------
// Tier 4 — legacy lifts table
// ---------------------------------------------------------------------------

async function tryLegacyLifts(userId: string): Promise<Partial<Record<V1Lift, string | null>>> {
  const dates: Partial<Record<V1Lift, string | null>> = {};

  for (const lift of V1_LIFT_POOL) {
    dates[lift] = null;
    const names = V1_EXERCISE_NAMES[lift];
    try {
      const { data } = await supabase
        .from('lifts')
        .select('performed_at, exercises!inner(name)')
        .eq('user_id', userId)
        .in('exercises.name', names)
        .order('performed_at', { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        dates[lift] = data[0].performed_at;
      }
    } catch {
      // ignore
    }
  }

  return dates;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch baseline status for a user.
 * Resolves from the best available source (RPC → strength_events → workouts → lifts).
 * Results are cached for 5 minutes to avoid stale "not complete" states persisting
 * after all lifts have been logged.
 */
export async function fetchBaselineStatus(userId: string): Promise<BaselineStatus> {
  // Return cached result if fresh
  const cached = _cache.get(userId);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
    return cached.result;
  }

  const cache = (result: BaselineStatus): BaselineStatus => {
    _cache.set(userId, { result, ts: Date.now() });
    return result;
  };

  // Tier 1: RPC
  const rpcDates = await tryRpc(userId);
  if (rpcDates) {
    log.info('[Baseline] resolved via RPC');
    return cache(calculateBaselineStatus(rpcDates));
  }

  // Tier 2: strength_events
  const seDates = await tryStrengthEvents(userId);
  if (seDates) {
    log.info('[Baseline] resolved via strength_events');
    return cache(calculateBaselineStatus(seDates));
  }

  // Tier 3: workouts.payload
  const wkDates = await tryWorkoutsPayload(userId);
  if (wkDates) {
    log.info('[Baseline] resolved via workouts.payload');
    return cache(calculateBaselineStatus(wkDates));
  }

  // Tier 4: legacy lifts table
  log.info('[Baseline] resolved via legacy lifts table');
  const legacyDates = await tryLegacyLifts(userId);
  return cache(calculateBaselineStatus(legacyDates));
}
